import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    DataServiceError,
    DefaultDataServiceConfig,
    EntityActionFactory,
    EntityOp,
    HttpUrlGenerator
} from '@ngrx/data';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as fromShared from '../../shared';
import { IGenericResponse } from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { EfaactDataService } from '../efaact.data-service';
import { PayrateOverride } from './payrate-override.model';

export const entityName = 'EmployeePayOvr';

@Injectable()
export class PayrateOverrideDataService extends EfaactDataService<PayrateOverride> {
    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        log: LoggingService,
        config: DefaultDataServiceConfig,
        store: Store
    ) {
        super(http, httpUrlGenerator, entityName, log, config, store);
    }

    protected save(
        update: Update<PayrateOverride>
    ): Observable<PayrateOverride> {
        this.log.verbose({
            type: `${this.entityName}DataService(PayrateOverrideDataService):save`,
            update
        });
        const item = {
            ...update.changes
        } as any;
        delete item.original;
        return this.http
            .put<IGenericResponse<PayrateOverride>>(this.entitiesUrl, {
                [`${this.entityName}List`]: [item]
            })
            .pipe(
                map(resp => {
                    this.log.verbose({
                        type: `${this.entityName}DataService(PayrateOverrideDataService):save:resp`,
                        resp
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(x => !!x).map(
                                        x => ({
                                            ...x,
                                            Status: {
                                                ...x.Status,
                                                MsgEntityID:
                                                    update.changes.original &&
                                                    update.changes.original.id
                                            }
                                        })
                                    )
                                })
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'PUT', url: this.entitiesUrl }
                            )
                        );
                        return {
                            ...update.changes.original,
                            Status:
                                resp.Items[0] && resp.Items[0].Status
                                    ? resp.Items[0].Status
                                    : {
                                          StatusCode: resp.StatusCode,
                                          Message: resp.StatusType
                                      }
                        } as PayrateOverride;
                    }
                    const af = new EntityActionFactory();
                    this.store.dispatch(
                        af.create(
                            this.entityName,
                            EntityOp.SAVE_UPSERT_MANY_SUCCESS,
                            resp.Items.map(x => ({
                                ...this.mapItems(x)
                            }))
                        )
                    );
                    return {
                        ...this.mapItems(resp.Items[0])
                    } as PayrateOverride;
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }
    mapItems(item: PayrateOverride): PayrateOverride {
        if (!item) {
            return;
        }
        return {
            ...item,
            id: item.Ovrid,
            EndDate: moment(item.EndDate).isValid()
                ? moment(item.EndDate).toDate()
                : null,
            MaintDate: moment(item.MaintDate).isValid()
                ? moment(item.MaintDate).toDate()
                : null,
            StartDate: moment(item.StartDate).isValid()
                ? moment(item.StartDate).toDate()
                : null
        };
    }
}
